import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import { Currency } from "app/models/currency.model";
import {
  DisplayedComboGroupStructure,
  emptyOverride,
  MenuSection,
  ModifierGroup,
  ModifierGroupOverride,
  ModifierGroupStructure,
  ModifierGroupType,
  ModifierProductStructure,
  OverrideInstruction,
  OverridePositionRequest,
  OverridePositionsMap,
  PositionTypeEnum,
  Product
} from 'app/models/menu.model';
import { buildNameTranslations, MenuOverrideService } from "app/services/menu-override.service";
import { Organization } from "app/models/organization.model";
import { MenuService } from 'app/services/menu.service';
import {RoleMembership, selectRole} from "app/reducers/context.selectors";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {Store} from "@ngrx/store";
import { MatExpansionPanel } from '@angular/material/expansion';
import {ModifierGroupInitValues} from "app/models/modifierGroupInitValues.model";
import {ChangeIsHiddenEvent} from "app/models/changeIsHiddenEvent.model";
import { ProductApiService } from 'app/services/product-api.service';
import {MatDialog} from "@angular/material/dialog";
import {
  ModifierGroupTypeModalComponent, ModifierGroupTypeModalData
} from "app/pages/menu/shared/modifier-group-type-modal/modifier-group-type-modal.component";
import {selectModifierGroupTypes, selectModifierGroupTypesDictionary} from "app/reducers/menu.selectors";
import {setModifierGroupTypes} from "app/reducers/menu.actions";
import {ModifierGroupTypeService} from "app/services/modifier-group-type.service";

@UntilDestroy()
@Component({
  selector: 'app-edit-modifier-group[saveForAll][saveForAllCombo][saveForAllProductsInComboGroup]',
  templateUrl: './edit-modifier-group.component.html',
  styleUrls: ['./edit-modifier-group.component.scss']
})
export class EditModifierGroupComponent implements OnInit {
  isBeingSaveProcessed = false;
  isEditingMode = false;
  iconTooltip: string;

  @Input() allowOverrides: boolean;
  @Input() comboGroupStructure?: DisplayedComboGroupStructure;
  @Input() modifierGroupStructure: ModifierGroupStructure | null;
  @Input() originalModifierGroupStructure: ModifierGroupStructure | null;
  @Input() currency: Currency | null;
  @Input() dictionaryModifierGroups: Map<string, ModifierGroup>;
  @Input() originalDictionaryModifierGroups: Map<string, ModifierGroup>;
  @Input() dictionaryProducts: Map<string, Product>;
  @Input() originalDictionaryProducts: Map<string, Product>;
  @Input() overridePositionsMap: OverridePositionsMap;
  @Input() currentOrganization: Organization;
  @Input() menuSections: MenuSection[];
  @Input() menuSectionId: string;
  @Input() productId: string;
  @Input() saveForMenuSections: string[];
  @Input() saveForAllCombo: boolean;
  @Input() saveForAll: boolean;
  @Input() saveForAllProductsInComboGroup: boolean;
  @Output() changeInformationMessageEvent = new EventEmitter<string[]>();
  @ViewChild(MatExpansionPanel) pannel: MatExpansionPanel;

  modifierGroupTypes: ModifierGroupType[] = [];
  modifierGroupTypesDictionary: Map<string, ModifierGroupType> = new Map();
  modifierGroup: ModifierGroup;
  originalModifierGroup: ModifierGroup;
  modifierGroupName: string;
  nameTranslations: {};
  nameTranslationKeys: string [];
  modifierGroupMin: number;
  modifierGroupMax: number;
  modifierGroupFreeQuantity: number;
  modifierGroupRequired: boolean;
  childModifiersHaveMinMaxRestrictions: boolean;
  isHidden: boolean;
  hideImages: boolean;
  initValues: ModifierGroupInitValues = {};
  changes = [];
  // Чтобы можно было одновременно редактировать min-max только у одного модификатора группы.
  editedModifierId: string;
  childrenIshidden:boolean[] = [];
  sortedModifiers: ModifierProductStructure[] = [];
  sortedOriginalModifiers: ModifierProductStructure[] = [];

  public roleMembership: RoleMembership | undefined = undefined;
  modifierGroupTypeId?: string;

  constructor(
    public dialog: MatDialog,
    private translate: TranslateService,
    private menuService: MenuService,
    public store: Store,
    private modifierGroupTypeService: ModifierGroupTypeService,
    private productApiService: ProductApiService,
  ) {
  }

  ngOnInit(): void {
    this.init();

    this.sortedModifiers = this.sortModiferProducts(this.modifierGroupStructure.modifierProducts);
    this.sortedOriginalModifiers = this.sortModiferProducts(this.originalModifierGroupStructure.modifierProducts);
    this.childrenIshidden = this.sortedModifiers.map(p => p.isHidden);

    this.store.select(selectRole).pipe(
      untilDestroyed(this)
    ).subscribe(roleMembership => {
      this.roleMembership = roleMembership;
    });

    this.store.select(selectModifierGroupTypes).pipe(
      untilDestroyed(this)
    ).subscribe(modifierGroupTypes => {
      this.modifierGroupTypes = modifierGroupTypes;
    });

    this.store.select(selectModifierGroupTypesDictionary).pipe(
      untilDestroyed(this)
    ).subscribe(modifierGroupTypesDictionary => {
      this.modifierGroupTypes = [...modifierGroupTypesDictionary.values()];
      this.modifierGroupTypesDictionary = modifierGroupTypesDictionary;
    });
  }

  init(): void {
    this.modifierGroup = this.dictionaryModifierGroups.get(this.modifierGroupStructure.modifierGroupId);
    this.originalModifierGroup = this.originalDictionaryModifierGroups.get(this.modifierGroupStructure.modifierGroupId);
    this.modifierGroupName = this.modifierGroup.name;
    this.nameTranslations = buildNameTranslations(
      this.modifierGroup.nameTranslations,
      this.currentOrganization.additionalLanguages);
    this.nameTranslationKeys = Object.keys(this.nameTranslations);
    this.modifierGroupMin = this.modifierGroupStructure.min;
    this.modifierGroupMax = this.modifierGroupStructure.max;
    this.modifierGroupFreeQuantity = this.modifierGroupStructure.freeQuantity ?? 0;
    this.modifierGroupRequired = this.modifierGroupStructure.required;
    this.childModifiersHaveMinMaxRestrictions = this.modifierGroupStructure.childModifiersHaveMinMaxRestrictions;
    this.hideImages = this.modifierGroupStructure.hideImages;
    this.isHidden = this.modifierGroupStructure.isHidden;
    this.modifierGroupTypeId = this.modifierGroup.modifierGroupTypeId;
    this.iconTooltip = this.translate.instant('COMMON.EDIT');
    this.setInitValues();
  }

  private setInitValues() {
    this.initValues.name = this.modifierGroupName;
    this.initValues.nameTranslations = {...this.nameTranslations};
    this.initValues.min = this.modifierGroupMin;
    this.initValues.max = this.modifierGroupMax;
    this.initValues.freeQuantity = this.modifierGroupFreeQuantity;
    this.initValues.required = this.modifierGroupRequired;
    this.initValues.childModifiersHaveMinMaxRestrictions = this.childModifiersHaveMinMaxRestrictions;
    this.initValues.hideImages = this.hideImages;
    this.initValues.isHidden = this.isHidden;
    this.initValues.modifierGroupTypeId = this.modifierGroupTypeId;
  }

  sortModiferProducts(modifierProducts: ModifierProductStructure[]) {
    return [...modifierProducts].sort(this.sortModifierProductsByOrder)
  }

  sortModifierProductsByOrder(p1: ModifierProductStructure, p2: ModifierProductStructure): number {
    if (p1.order > p2.order) {
      return 1;
    }
    if (p1.order < p2.order) {
      return -1;
    }
    return 0;
  }

  async editModifierGroup(event: Event) {
    if(!this.allowOverrides){
      return;
    }
    event.stopPropagation();
    if (this.isEditingMode) {
      if (this.validate()) {
        await this.saveModifierGroup();
        await this.saveModifierGroupStructure();
        this.toggleEditMode();
        this.setInitValues();
      }
    } else {
      this.pannel.open();
      this.toggleEditMode();
    }
  }

  toggleEditMode() {
    this.isEditingMode = !this.isEditingMode;
    this.iconTooltip = this.translate.instant(this.isEditingMode ? 'COMMON.SAVE' : 'COMMON.EDIT');
  }

  onClickIsHidden() {
    for(let i = 0; i < this.childrenIshidden.length; i++){
      this.childrenIshidden[i] = this.isHidden;
    }
  }

  async saveModifierGroup() {
    let modifierGroupOverride: ModifierGroupOverride;
    let isOverrideExists: boolean;
    let modifierGroupOverridePosition = this.overridePositionsMap.getOverride(PositionTypeEnum.ModifierGroup, this.modifierGroupStructure.modifierGroupId);
    // if override position does not exists create a new one
    if (!modifierGroupOverridePosition) {
      modifierGroupOverridePosition = {
        positionType: PositionTypeEnum.ModifierGroup,
        positionId: this.modifierGroupStructure.modifierGroupId,
        override: emptyOverride,
        menuSectionId: this.menuSectionId,
      };
      modifierGroupOverride = {};
    } else {
      // if override position exists update it anyway no matter has changes or not
      isOverrideExists = true;
      modifierGroupOverride = JSON.parse(modifierGroupOverridePosition.override) as ModifierGroupOverride;
    }

    const overrideInstructions: OverrideInstruction[] = []
    if (this.initValues.name.trim() !== this.modifierGroupName.trim()) {
      MenuOverrideService.overrideTextFiled(this.modifierGroup, this.originalModifierGroup, 'name', this.modifierGroupName, isOverrideExists, overrideInstructions);
    }

    for (const lang in this.nameTranslations) {
      const value = this.nameTranslations[lang];
      if (this.initValues.nameTranslations[lang] !== value) {
        MenuOverrideService.overrideTranslationFiled(
          this.modifierGroup, this.originalModifierGroup, 'nameTranslations', lang, value, isOverrideExists, modifierGroupOverride, overrideInstructions);
      }
    }

    if(this.initValues.modifierGroupTypeId !== this.modifierGroupTypeId) {
      MenuOverrideService.overrideTextFiled(this.modifierGroup, this.originalModifierGroup, 'modifierGroupTypeId', this.modifierGroupTypeId, isOverrideExists, overrideInstructions, true);
    }

    if (overrideInstructions.length) {
      const newModiferGroupOverridePosition: OverridePositionRequest = {
        ...modifierGroupOverridePosition,
        instructions: overrideInstructions
      };
      await this.menuService.saveOverridePositions(this.menuSectionId, [newModiferGroupOverridePosition], this.saveForMenuSections);
    }
  }

  async saveModifierGroupStructure() {
    this.isBeingSaveProcessed = true;
    let productIds: string[] = [this.productId];
    if (this.saveForAll || this.saveForAllCombo){
      const externalProductIdsResponse = await this.productApiService.getExternalProductIdsByModifierGroup(
        this.menuSections[0].menuId,
        this.modifierGroup.externalId,
        this.saveForAllCombo);

      productIds = externalProductIdsResponse.data ?? [];
    }

    if(this.saveForAllProductsInComboGroup && this.comboGroupStructure){
      this.comboGroupStructure.products.forEach(product => {
        if(!product.isHidden && product.externalProductId !== this.productId){
          productIds.push(product.externalProductId)
        }
      });
    }

    const overridePositionRequest: OverridePositionRequest[] = [];

    for (let externalProductId of productIds) {
      const itemOverridePositionRequests =
        this.buildOverridePositionRequest(externalProductId, this.modifierGroup.externalId);
      itemOverridePositionRequests.forEach(itemOverridePositionRequest => overridePositionRequest.push(itemOverridePositionRequest))
    }

    if (overridePositionRequest.length > 0) {
      await this.menuService.saveOverridePositions(this.menuSectionId, overridePositionRequest, this.saveForMenuSections);
    }

    this.isBeingSaveProcessed = false;
  }

  buildOverridePositionRequest(externalProductId: string, modifierGroupId: string): OverridePositionRequest[]
  {
    let isOverrideExists: boolean;
    const positionId = `${externalProductId}_${modifierGroupId}`;
    let modifierGroupStructureOverridePosition =
      this.overridePositionsMap.getOverride(PositionTypeEnum.ModifierGroupStructure, positionId);
    // if override position does not exists create a new one
    if (!modifierGroupStructureOverridePosition) {
      modifierGroupStructureOverridePosition = {
        positionType: PositionTypeEnum.ModifierGroupStructure,
        positionId: positionId,
        override: emptyOverride,
        menuSectionId: this.menuSectionId,
      };
    } else {
      // if override position exists update it anyway no matter has changes or not
      isOverrideExists = true;
    }

    const overrideInstructions: OverrideInstruction[] = []
    if (this.initValues.min !== this.modifierGroupMin) {
      MenuOverrideService.overrideNumberFiled(this.modifierGroupStructure, this.originalModifierGroupStructure, 'min', this.modifierGroupMin, isOverrideExists, overrideInstructions);
    }
    if (this.initValues.max !== this.modifierGroupMax) {
      MenuOverrideService.overrideNumberFiled(this.modifierGroupStructure, this.originalModifierGroupStructure, 'max', this.modifierGroupMax, isOverrideExists, overrideInstructions);
    }
    if (this.initValues.freeQuantity !== this.modifierGroupFreeQuantity) {
      MenuOverrideService.overrideNumberFiled(this.modifierGroupStructure, this.originalModifierGroupStructure, 'freeQuantity', this.modifierGroupFreeQuantity, isOverrideExists, overrideInstructions);
    }
    if (this.initValues.required !== this.modifierGroupRequired) {
      MenuOverrideService.overrideBooleanFiled(this.modifierGroupStructure, this.originalModifierGroupStructure, 'required', this.modifierGroupRequired, isOverrideExists, overrideInstructions);
    }
    if (this.initValues.childModifiersHaveMinMaxRestrictions !== this.childModifiersHaveMinMaxRestrictions) {
      MenuOverrideService.overrideBooleanFiled(
        this.modifierGroupStructure,
        this.originalModifierGroupStructure,
        'childModifiersHaveMinMaxRestrictions',
        this.childModifiersHaveMinMaxRestrictions,
        isOverrideExists,
        overrideInstructions);
    }
    if(this.initValues.hideImages !== this.hideImages){
      MenuOverrideService.overrideBooleanFiled(
        this.modifierGroupStructure,
        this.originalModifierGroupStructure,
        'hideImages',
        this.hideImages,
        isOverrideExists,
        overrideInstructions);
    }

    let overrideChildrenHidden: OverridePositionRequest[] = [];

    if (this.initValues.isHidden !== this.isHidden) {
      MenuOverrideService.overrideBooleanFiled(
        this.modifierGroupStructure,
        this.originalModifierGroupStructure,
        'isHidden',
        this.isHidden,
        isOverrideExists,
        overrideInstructions);
      overrideChildrenHidden = this.editChildrenModifiersHidden(externalProductId);
    }

    if (overrideInstructions.length > 0) {
      const newModifierGroupStructureOverridePosition: OverridePositionRequest = {
        ...modifierGroupStructureOverridePosition,
        instructions: overrideInstructions
      };

      return [newModifierGroupStructureOverridePosition, ...overrideChildrenHidden];
    }

    return [];
  }

  private editChildrenModifiersHidden(externalProductId: string) : OverridePositionRequest[] {

    const overridePositionRequests: OverridePositionRequest[] = [];

    for(let i = 0; i < this.modifierGroupStructure.modifierProducts.length; i++){
      const modifier = this.sortedModifiers[i];

      const originalModifier = this.sortedOriginalModifiers[i];

      const isHidden = this.childrenIshidden[i];

      let isOverrideExists: boolean = false;
      const positionId = `${externalProductId}_${modifier.modifierId}`;
      let productStructureOverridePosition =
        this.overridePositionsMap.getOverride(PositionTypeEnum.ModifierProductStructure, positionId);
      // if override position does not exists create a new one
      if (!productStructureOverridePosition) {
        productStructureOverridePosition = {
          positionType: PositionTypeEnum.ModifierProductStructure,
          positionId: positionId,
          override: emptyOverride,
          menuSectionId: this.menuSectionId,
        };
      } else {
        // if override position exists update it anyway no matter has changes or not
        isOverrideExists = true;
      }
      const overrideInstructions: OverrideInstruction[] = [];
      MenuOverrideService.overrideBooleanFiled(
        modifier,
        originalModifier,
        'isHidden',
        isHidden,
        isOverrideExists,
        overrideInstructions);
      const newModifierProductOverridePosition: OverridePositionRequest = {
        ...productStructureOverridePosition,
        instructions: overrideInstructions
      };
      overridePositionRequests.push(newModifierProductOverridePosition)
    }

    return overridePositionRequests;
  }

  validate() {
    if (this.originalModifierGroupStructure.required && !this.modifierGroupRequired) {
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.REQUIRED_MODIFIER_GROUP');
      alert(validationMessage);
      this.modifierGroupRequired = this.originalModifierGroupStructure.required;
      return false;
    }

    if (this.modifierGroupMin < 1 && this.modifierGroupRequired) {
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.REQUIRED_MODIFIER_GROUP');
      alert(validationMessage);
      this.modifierGroupMin = 1;
      return false;
    }

    if (this.modifierGroupMin > 0 && !this.modifierGroupRequired) {
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.REQUIRED_MODIFIER_GROUP');
      alert(validationMessage);
      this.modifierGroupMin = 0;
      return false;
    }

    if (this.modifierGroupStructure.required && this.modifierGroupMax < 1){
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.MAX_REQUIRED_MODIFIER_GROUP');
      alert(validationMessage);
      this.modifierGroupMax = this.modifierGroupStructure.max;
      return false;
    }

    if (this.modifierGroupMin < this.originalModifierGroupStructure.min) {
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.MIN_MODIFIER_GROUP');
      alert(validationMessage);
      this.modifierGroupMin = this.originalModifierGroupStructure.min;
      return false;
    }

    if (this.originalModifierGroupStructure.max > 0 && this.modifierGroupMax > this.originalModifierGroupStructure.max) {
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.MAX_MODIFIER_GROUP');
      alert(validationMessage);
      this.modifierGroupMax = this.originalModifierGroupStructure.max;
      return false;
    }

    if (this.modifierGroupMin > this.modifierGroupMax) {
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.MIN_GREATER_MAX_MODIFIER_GROUP');
      alert(validationMessage);
      this.modifierGroupMin = this.modifierGroupMax;
      return false;
    }

    if (this.modifierGroupFreeQuantity < 0) {
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.FREE_QUANTITY_NEGATIVE_MODIFIER_GROUP');
      alert(validationMessage);
      this.modifierGroupFreeQuantity = 0;
      return false;
    }

    if (this.modifierGroupMax > 0 && this.modifierGroupFreeQuantity > this.modifierGroupMax) {
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.FREE_QUANTITY_GREATER_MAX_MODIFIER_GROUP');
      alert(validationMessage);
      this.modifierGroupFreeQuantity = this.modifierGroupMax;
      return false;
    }
    return true;
  }

  getOriginalModifierProduct(index: number) {
    return this.sortModiferProducts(this.originalModifierGroupStructure.modifierProducts)[index];
  }

  isNameDifferent() {
    return this.modifierGroupName.trim() !== this.originalModifierGroup.name.trim();
  }

  isModifierGroupMinDifferent() {
    return this.originalModifierGroupStructure.min !== this.modifierGroupStructure.min;
  }

  isModifierGroupMaxDifferent() {
    return this.originalModifierGroupStructure.max !== this.modifierGroupStructure.max;
  }

  isModifierGroupFreeQuantityDifferent() {
    return (this.originalModifierGroupStructure.freeQuantity ?? 0) !== (this.modifierGroupStructure.freeQuantity ?? 0);
  }

  isModifierGroupRequiredDifferent() {
    return this.originalModifierGroupStructure.required !== this.modifierGroupStructure.required;
  }

  isModifierGroupChildModifiersHaveMinMaxRestrictionsDifferent() {
    return this.originalModifierGroupStructure.childModifiersHaveMinMaxRestrictions !== this.initValues.childModifiersHaveMinMaxRestrictions;
  }

  isModifierGroupHideImagesDifferent(){
    return this.modifierGroupStructure.hideImages;
  }

  isNameTranslationDifferent(lang: string) {
    const currentValue = this.initValues.nameTranslations[lang];
    const originalValue = this.originalModifierGroup.nameTranslations[lang] ?? '';
    return currentValue != originalValue;
  }

  isModifierGroupMinInputDifferent() {
    return this.originalModifierGroupStructure.min !== this.modifierGroupMin;
  }

  isModifierGroupMaxInputDifferent() {
    return this.originalModifierGroupStructure.max !== this.modifierGroupMax;
  }

  isModifierGroupFreeQuantityInputDifferent() {
    return (this.originalModifierGroupStructure.freeQuantity ?? 0) !== this.modifierGroupFreeQuantity;
  }

  onChangeInformationMessage() {
    this.changes = [];
    if (this.initValues.min != this.modifierGroupMin) {
      this.changes.push(`"Modifier Group ${this.modifierGroupName} Min"`)
    }
    if (this.initValues.max != this.modifierGroupMax) {
      this.changes.push(`"Modifier Group ${this.modifierGroupName} Max"`)
    }
    if (this.initValues.freeQuantity != this.modifierGroupFreeQuantity) {
      this.changes.push(`"Modifier Group ${this.modifierGroupName} Free"`)
    }
    if (this.initValues.required != this.modifierGroupRequired) {
      this.changes.push(`"Modifier Group ${this.modifierGroupName} Required"`)
    }
    if (this.initValues.name != this.modifierGroupName) {
      this.changes.push(`"Modifier Group ${this.modifierGroupName} Name"`)
    }
    if (this.initValues.isHidden != this.modifierGroupStructure.isHidden) {
      this.changes.push(`"Modifier Group ${this.modifierGroupName} Name"`)
    }
    this.changeInformationMessageEvent.emit(this.changes);
  }

  onChildrenChangeIsHidden(event: ChangeIsHiddenEvent){
    if(this.isHidden && !event.isHidden){
      this.isHidden = false;
      this.initValues.isHidden = false;
    }
    this.childrenIshidden[event.index] = event.isHidden;
  }

  trackByFn(index, item: ModifierProductStructure) {
    return item.modifierId;
  }

  changeModifierGroupNameTranslations(event: any, lang: string) {
    this.nameTranslations[lang] = event.target.value;
  }

  clearName() {
    this.modifierGroupName = this.originalModifierGroup.name;
  }

  clearNameTranslation(lang: string) {
    this.nameTranslations[lang] = this.originalModifierGroup.nameTranslations[lang] ?? '';
  }

  openModifierGroupTypeModal(create: boolean){
    this.dialog.open(ModifierGroupTypeModalComponent, {
      data: {
        menuId: this.menuSections[0].menuId,
        modifierGroupType: !create && this.modifierGroupTypeId ? this.modifierGroupTypesDictionary.get(this.modifierGroupTypeId) : {},
      } as ModifierGroupTypeModalData
    }).afterClosed().subscribe(result => {
      if (result.created){
        const created = result.created as ModifierGroupType;
        this.modifierGroupTypesDictionary.set(created.id, created);
        this.store.dispatch(setModifierGroupTypes([...this.modifierGroupTypesDictionary.values()]));
        this.modifierGroupTypeId = created.id;
      } else if(result.updated){
        const updated = result.updated as ModifierGroupType;
        this.modifierGroupTypesDictionary.set(updated.id, updated);
        this.store.dispatch(setModifierGroupTypes([...this.modifierGroupTypesDictionary.values()]));
      } else if(result.deleted){
        const deletedId = result.deleted as string;
        this.onRemoveMgType(deletedId);
      }
    })
  }

  onMgTypePicked(id: string) {
    this.modifierGroupTypeId = id;
  }

  async onRemoveMgType(deletedId: string) {
    if(this.modifierGroupTypeId && confirm(this.translate.instant('MENU.DISHES.NEW.CONFIRM_DELETE_TAG'))){
      await this.modifierGroupTypeService.delete(this.modifierGroupTypeId);
      this.modifierGroupTypeId = null;
      this.modifierGroupTypesDictionary.delete(deletedId);
      this.store.dispatch(setModifierGroupTypes([...this.modifierGroupTypesDictionary.values()]));
    }
  }

  getTypeName() {
    if(this.modifierGroupTypeId == null){
      return "";
    }
    return this.modifierGroupTypesDictionary.get(this.initValues.modifierGroupTypeId)?.typeName ?? "";
  }
}
